import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { extractScanCode } from "../services/selfCheckin.service";
import {
  validateVoucher,
  redeemVoucher,
  logVoucherActivityWithIP,
  isTicketCode,
  validateTicketReward,
  redeemTicketReward,
  fetchFacilityRedemptionCandidates,
} from "../services/redemption.service";
import type {
  RedeemVoucherResult,
  VoucherValidationResult,
  RedeemVoucherInput,
  RedemptionSearchEntry,
} from "../services/redemption.service";
import RedemptionEntrySearch from "../components/redemption/RedemptionEntrySearch";
import {
  enqueue as enqueueRedemption,
  getQueue,
  processQueue,
  removeEntry,
  QUEUE_BOUNDS,
  type QueuedRedemption,
  type QueuedSuccessDetail,
} from "../services/redemptionQueue";
import {
  formatCurrency,
  formatDate,
  formatLocalDate,
} from "../utils/formatters";

// Booth/vendor info is no longer collected for event vouchers
// Only non-event vouchers may use booth fields if needed in the future

const VoucherRedemption: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [voucherCode, setVoucherCode] = useState("");
  const [redeemedBy, setRedeemedBy] = useState("");
  const [appliedAmount, setAppliedAmount] = useState("");
  const [_boothId, setBoothId] = useState("");
  const [_vendorName, setVendorName] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  // Multi-org voucher state
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  // R1: the entry picked in the server-side search (ALL / MULTIPLE offer vouchers)
  const [selectedSearchEntry, setSelectedSearchEntry] =
    useState<RedemptionSearchEntry | null>(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] =
    useState<VoucherValidationResult | null>(null);
  const [result, setResult] = useState<RedeemVoucherResult | null>(null);
  const [errorModal, setErrorModal] = useState<{
    show: boolean;
    message: string;
  }>({ show: false, message: "" });
  const [clientIp, setClientIp] = useState<string | undefined>(undefined);
  // Offline retry queue: persists across reloads in localStorage. The
  // banner renders when this is non-empty. We re-read on mount, on every
  // submit (success or queueing), on `online`/`offline` events, and after
  // each processQueue() pass.
  const [retryQueue, setRetryQueue] = useState<QueuedRedemption[]>(() =>
    getQueue(),
  );
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null);
  const scannerRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Check if this is an event-based voucher. Voucher CTA columns are now
  // strictly split — cta_id only for event CTAs, org_cta_id only for org
  // CTAs. event_id remains the canonical signal that this is an event-
  // context voucher (set on event-CTA-sourced and direct event vouchers;
  // never set for org-CTA-sourced vouchers).
  const isEventVoucher = !!validationResult?.voucher?.event_id;
  // A TK- ticket reward (redeemed at a vendor booth) has no event_id, but it is ALSO reimbursed by
  // business name — so it requires the Business Name field just like an event voucher (2026-08-12).
  // The name is stored via redeem_ticket_reward's redeemed_by, which the redemption report surfaces
  // under "Business Name" (vendor_name || booth_id || redeemed_by).
  const isTicketReward = isTicketCode(voucherCode);
  // RETAIL ticket reward: a ticket whose event is NOT a gun show. The operator sees exactly the
  // ordinary retail redemption — no Business Name field — and the event tie stays entirely in the
  // back end, which is the owner's requirement (2026-09-10).
  //
  // This used to be gated on the event having an associated ORGANISATION, which looked equivalent
  // but was not: events.organization_id is NULL on all 146 PROD events and only 77 have an
  // event_organizations row, so the retail branch never engaged and EVERY ticket was asked for a
  // Business Name. Being a gun show is a property of the event, so that is what it now tests.
  //
  // Fail-closed: a ticket whose event type is unknown (no event on the token) keeps the Business
  // Name flow, which is the pre-existing behaviour for that case.
  const ticketEventType = validationResult?.voucher?.event_type;
  const retailTicket =
    isTicketReward && !!ticketEventType && ticketEventType !== "gun_show";
  const requireBusinessName =
    (isEventVoucher || isTicketReward) && !retailTicket;

  // Check if location selection is required (org-level voucher with multiple locations)
  const requiresLocationSelection =
    validationResult?.voucher?.requires_location_selection ?? false;
  const availableLocations =
    validationResult?.voucher?.available_locations ?? [];

  // Filter locations based on search term
  const filteredLocations = availableLocations.filter((loc) =>
    loc.name.toLowerCase().includes(locationSearchTerm.toLowerCase()),
  );

  // Get selected location name
  const selectedLocationName = availableLocations.find(
    (loc) => loc.id === selectedLocationId,
  )?.name;

  // Check if organization selection is required (multi-org vouchers)
  const requiresOrganizationSelection =
    validationResult?.voucher?.requires_organization_selection ?? false;
  const availableOrganizations =
    validationResult?.voucher?.available_organizations ?? [];
  // R1 (plan v4 §3.3): ALL / MULTIPLE offer vouchers search the server instead of a preloaded (1,000-row
  // capped) list.
  const organizationSearch =
    validationResult?.voucher?.organization_search ?? false;
  // Shared facility (incident 2026-10-24): the facility ID belongs to more than one organization, so the partner
  // picks the store from a short list (name, city, state). The server re-checks the choice.
  const facilityShared = validationResult?.voucher?.facility_shared ?? false;

  // Filter organizations based on search term (name, address, city, state)
  const filteredOrganizations = availableOrganizations.filter(
    (org) =>
      org.name.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
      (org.address &&
        org.address.toLowerCase().includes(orgSearchTerm.toLowerCase())) ||
      (org.city &&
        org.city.toLowerCase().includes(orgSearchTerm.toLowerCase())) ||
      (org.state &&
        org.state.toLowerCase().includes(orgSearchTerm.toLowerCase())),
  );

  // Get selected organization name
  const selectedOrganization = availableOrganizations.find(
    (org) => org.id === selectedOrganizationId,
  );

  // Handle click outside of dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setShowLocationDropdown(false);
      }
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target as Node)
      ) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch client IP address on mount (fire-and-forget, used for activity logging)
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => {
        if (data?.ip) {
          setClientIp(data.ip);
        }
      })
      .catch(() => {
        // IP lookup failed - continue without it
      });
  }, []);

  // #5: use the shared robust extractor so a scanned Event Ticket QR — whose payload is a
  // `/t/<code>` check-in URL — resolves to the bare TK- code, exactly like the manual/URL path.
  // Then isTicketCode(TK-) routes it to the ticket-reward flow (which enforces the check-in gate).
  const extractVoucherCode = (input: string): string =>
    extractScanCode(input).toUpperCase().trim();

  useEffect(() => {
    return () => {
      if (html5QrcodeRef.current) {
        html5QrcodeRef.current.stop().catch((err) => {
          if (!err?.message?.includes("not running")) {
            console.error("Scanner cleanup error:", err);
          }
        });
      }
    };
  }, []);

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      setVoucherCode(code.toUpperCase());
      handleValidate(code.toUpperCase());
    }
  }, [searchParams]);

  /**
   * Apply a successful queued redemption to the page state so the user
   * sees the same confirmation screen they'd get from an immediate
   * (online) redemption. The success render reads voucherCode,
   * appliedAmount, redeemedBy, and isEventVoucher (derived from
   * validationResult.voucher.event_id). We rebuild a synthetic
   * validationResult from the queue entry's displayContext so the page
   * doesn't have to re-validate the (now-redeemed) voucher.
   */
  const applyQueuedSuccess = (detail: QueuedSuccessDetail) => {
    setVoucherCode(detail.voucherCode);
    setAppliedAmount(detail.appliedAmount.toString());
    setRedeemedBy(detail.redeemedBy);
    // Synthetic validationResult: only the fields the success page reads.
    // event_id presence is what drives the "Vendor Redemption #" vs "Redeemed By"
    // label switch. We don't need org/event lookups since the page is past
    // the form-fill stage.
    setValidationResult({
      valid: true,
      voucher: {
        voucher_code: detail.voucherCode,
        discount_value:
          detail.displayContext?.discountValue ?? detail.appliedAmount,
        voucher_name: "",
        end_date: 0,
        start_date: 0,
        is_redeemed: true,
        // event_id is what `isEventVoucher` checks. Set a placeholder when
        // the queued payload was for an event voucher so the success page
        // shows "Vendor Redemption #".
        ...(detail.displayContext?.isEventVoucher
          ? { event_id: "queued-success" }
          : {}),
      },
    });
    setResult({
      success: true,
      message: "Voucher redeemed!",
      voucherCode: detail.voucherCode,
      appliedAmount: detail.appliedAmount,
      discountValue: detail.displayContext?.discountValue,
    });
  };

  // Process the retry queue when the browser regains connectivity, and
  // run an initial pass on mount in case the user reopened the page after
  // offline submissions persisted to localStorage.
  useEffect(() => {
    const runQueuePass = async () => {
      if (isProcessingQueue) {
        return;
      }
      const current = getQueue();
      const hasPending = current.some(
        (e) => e.status === "pending" || e.status === "retrying",
      );
      if (!hasPending) {
        setRetryQueue(current);
        return;
      }
      setIsProcessingQueue(true);
      try {
        await processQueue({
          onSuccess: (entry, detail) => {
            console.log(`Queued redemption succeeded: ${entry.voucherCode}`);
            applyQueuedSuccess(detail);
          },
          onAlreadyRedeemed: (entry) => {
            console.log(
              `Queued redemption resolved: already redeemed (${entry.voucherCode})`,
            );
            // Same screen as a fresh success — the prior submit went
            // through, the user just needs confirmation. We synthesize a
            // success detail from the entry's persisted data.
            const isEvent = entry.displayContext?.isEventVoucher ?? false;
            applyQueuedSuccess({
              voucherCode: entry.voucherCode,
              appliedAmount:
                entry.payload.applied_amount ??
                entry.displayContext?.discountValue ??
                0,
              redeemedBy: isEvent
                ? entry.payload.vendor_name || entry.payload.booth_id || ""
                : entry.payload.redeemed_by || "",
              displayContext: entry.displayContext,
            });
          },
        });
      } finally {
        setIsProcessingQueue(false);
        setRetryQueue(getQueue());
      }
    };
    const onOnline = () => {
      runQueuePass();
    };
    const onOffline = () => {
      // Refresh the banner state — UI shows different copy when offline.
      setRetryQueue(getQueue());
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    runQueuePass();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // Disable the lint rule — runQueuePass closes over fresh state but we
    // genuinely want this to run only on mount + connectivity events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = (message: string) => {
    setErrorModal({ show: true, message });
  };

  const closeError = () => {
    setErrorModal({ show: false, message: "" });
  };

  const handleValidate = async (code?: string) => {
    const codeToValidate = code || voucherCode;
    if (!codeToValidate || codeToValidate.length < 6) {
      showError("Please enter a valid voucher code (minimum 6 characters)");
      return;
    }

    setLoading(true);
    setValidationResult(null);
    setResult(null);

    try {
      // Event Ticket (TK-) codes resolve their linked reward via the narrow server adapter; every other
      // code is a voucher_code and takes the unchanged path. Tokens are a reserved prefix, so a TK- code
      // can never be a real voucher_code (it would otherwise just hit "not found").
      const validation = isTicketCode(codeToValidate)
        ? await validateTicketReward(codeToValidate)
        : await validateVoucher(codeToValidate, clientIp);
      setValidationResult(validation);
      if (validation.valid && validation.voucher) {
        setAppliedAmount(validation.voucher.discount_value.toString());
      }
    } catch (error: unknown) {
      console.error("Validation error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to validate voucher";
      setValidationResult({ valid: false, error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!validationResult?.valid || !validationResult.voucher) {
      showError("Please validate the voucher first");
      return;
    }

    // A non-monetary reward (e.g. an "Entry into a Drawing" — an Event Drawing Entry) has $0 redeemable
    // value: there is no amount to apply, so redeem it for 0 and skip the positive-amount checks. Positive
    // vouchers always have discount_value > 0, so this branch never affects the existing money path.
    const isNonMonetary = (validationResult.voucher.discount_value ?? 0) === 0;
    let amount = 0;
    if (!isNonMonetary) {
      // Validate applied amount
      amount = parseFloat(appliedAmount);
      if (isNaN(amount) || amount <= 0) {
        showError("Applied Voucher Amount must be a valid positive number");
        return;
      }

      // Check if amount exceeds discount value
      if (amount > validationResult.voucher.discount_value) {
        showError(
          `Applied Voucher Amount must be equal to or less than the Discount Value of ${formatCurrency(validationResult.voucher.discount_value)}`,
        );
        return;
      }

      // Validate decimal places (max 2)
      const decimalParts = appliedAmount.split(".");
      if (decimalParts.length > 1 && decimalParts[1].length > 2) {
        showError("Applied Voucher Amount can have at most 2 decimal places");
        return;
      }
    }

    // Business Name is required for event vouchers AND ticket rewards (both are reimbursed by
    // business name, so booth number is no longer collected — reverted 2026-08-12 per Rob).
    if (requireBusinessName && !redeemedBy.trim()) {
      showError("Business Name is required");
      return;
    }

    // Validate location selection if required for multi-location organizations
    if (requiresLocationSelection && !selectedLocationId) {
      showError("Please select a redemption location");
      return;
    }

    // Validate organization selection if required for multi-org vouchers
    if (requiresOrganizationSelection && !selectedOrganizationId) {
      showError(
        facilityShared
          ? "Please choose the store where this voucher is being redeemed"
          : "Please select your organization",
      );
      return;
    }

    setLoading(true);

    // Event Ticket reward (TK-): redeem via the narrow server adapter. A reward is NOT an event-vendor
    // voucher (no event_id) and the placeholder envelope is ALL_ORGANIZATIONS, so the vendor#/location/org
    // gates above are already inert; the amount cap (vs the reward value) already applied. The server core
    // performs the atomic redeem + activity log; the internal reward code is never surfaced. The voucher_code
    // path's client-IP attempt-log and offline-queue are intentionally not reused here (business failures
    // wouldn't queue anyway).
    if (isTicketCode(voucherCode)) {
      try {
        const tkOrgEntry = availableOrganizations.find(
          (o) => o.id === selectedOrganizationId,
        );
        const r = await redeemTicketReward({
          code: voucherCode,
          // Retail ticket → the event's organization is the redeemer (no Business Name typed).
          redeeming_organization_id: retailTicket
            ? validationResult.voucher?.organization_id
            : tkOrgEntry?.organization_id ||
              selectedOrganizationId ||
              undefined,
          applied_amount: amount,
          // Retail → never a typed name. The event's organisation when there is one,
          // otherwise the event itself, so the redemption still records WHAT it was
          // redeemed against rather than nothing.
          redeemed_by: retailTicket
            ? validationResult.voucher?.organization_name ||
              validationResult.voucher?.event_name ||
              undefined
            : redeemedBy.trim() || undefined,
        });
        if (r.success) {
          setAppliedAmount((r.applied_amount ?? amount).toString());
          setResult({ success: true });
        } else {
          showError(r.error || "This reward could not be redeemed.");
        }
      } catch {
        showError("Redemption failed. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Resolve org/location IDs from the selected entry
    // When a location is selected from the org dropdown, use its parent organization_id for the FK
    // and its location_id for the location reference. Built BEFORE the try so the catch-block offline
    // fallback can reuse the exact same payload (it used to reference try-scoped consts).
    const selectedOrgEntry = organizationSearch
      ? selectedSearchEntry
        ? {
            id: selectedSearchEntry.entry_id,
            organization_id: selectedSearchEntry.organization_id,
            location_id: selectedSearchEntry.location_id ?? undefined,
          }
        : undefined
      : availableOrganizations.find((org) => org.id === selectedOrganizationId);
    // The organization (and store) this facility voucher unambiguously resolves to, when the page knows it
    // (redemption_context, set only when the facility belongs to exactly one organization). Never used when the
    // partner had to pick an organization/store: their pick is what is sent.
    const knownContext =
      !requiresOrganizationSelection && !organizationSearch
        ? validationResult.voucher.redemption_context
        : undefined;
    const resolvedOrgId =
      selectedOrgEntry?.organization_id ||
      selectedOrganizationId ||
      knownContext?.organization_id ||
      undefined;
    const resolvedLocId =
      selectedOrgEntry?.location_id ||
      selectedLocationId ||
      (requiresLocationSelection ? undefined : knownContext?.location_id) ||
      undefined;

    const redemptionPayload: RedeemVoucherInput = {
      voucher_code: voucherCode,
      redeemed_by: isEventVoucher ? undefined : redeemedBy.trim() || undefined,
      // Event vouchers: the operator enters the vendor's BUSINESS NAME (reverted from the
      // numeric booth # on 2026-08-12 — vendors are reimbursed by business name). Stored in
      // vendor_name; booth_id is no longer collected.
      vendor_name: isEventVoucher ? redeemedBy.trim() || undefined : undefined,
      applied_amount: amount,
      redemption_location_id: resolvedLocId,
      redeeming_organization_id: resolvedOrgId,
      client_ip: clientIp,
      // Wire v2 (plan v4 §2.7): name the picked entry; the server derives the pair from it and refuses a
      // contradiction. The pair above is still sent so an older server records the same attribution.
      ...(organizationSearch && selectedSearchEntry
        ? { wire_version: 2 as const, entry_id: selectedSearchEntry.entry_id }
        : {}),
    };

    try {
      // Log redemption attempt with IP (fire-and-forget)
      if (validationResult.voucher) {
        logVoucherActivityWithIP(
          "redemption_attempted",
          voucherCode,
          "pending",
          undefined,
          { applied_amount: amount },
          clientIp,
        );
      }

      // Snapshot of state needed to render the post-success page if/when
      // a retry resolves. Persisted with the queue entry so a reload
      // while offline doesn't lose the context needed for the success
      // screen.
      const queueDisplayContext = {
        isEventVoucher,
        discountValue: validationResult.voucher.discount_value,
      };

      const redemptionResult = await redeemVoucher(redemptionPayload);
      // Mig 1540: a statewide / nationwide voucher redeemed without a store after its show's window closed: show the
      // store search (the area's locator stores) instead of an error, keep the form, and let the partner re-submit.
      if (
        !redemptionResult.success &&
        redemptionResult.errorCode === "context_required" &&
        organizationSearch &&
        !requiresOrganizationSelection
      ) {
        setValidationResult({
          ...validationResult,
          voucher: {
            ...validationResult.voucher,
            requires_organization_selection: true,
          },
        });
        setResult(null);
        showError("Choose the store where this voucher is being redeemed.");
        return;
      }
      // The server could not tell which store is redeeming (the facility ID is on more than one organization):
      // show the store list instead of a dead-end error, keep the form, and let the partner re-submit.
      if (
        !redemptionResult.success &&
        (redemptionResult.errorCode === "context_required" ||
          redemptionResult.errorCode === "needs_reselection") &&
        !facilityShared &&
        !organizationSearch
      ) {
        const shared = await fetchFacilityRedemptionCandidates(
          validationResult.voucher.facility_ids,
        );
        if (shared && shared.candidates.length > 0) {
          const voucherNow = validationResult.voucher;
          setValidationResult({
            ...validationResult,
            voucher: {
              ...voucherNow,
              facility_shared: true,
              requires_organization_selection: true,
              requires_location_selection: false,
              available_locations: undefined,
              redemption_context: undefined,
              available_organizations: shared.candidates.map((c) => ({
                id: c.id,
                name: c.name,
                address: c.address,
                city: c.city,
                state: c.state,
                organization_id: c.organization_id,
                location_id: c.location_id,
                organization_name: c.organization_name,
              })),
            },
          });
          setSelectedOrganizationId("");
          setSelectedLocationId("");
          setResult(null);
          return;
        }
        // No store list could be loaded: say so, instead of asking the partner to pick from a list that isn't shown.
        setResult(null);
        showError(
          "This voucher's retailer ID is listed under more than one store, and the list of stores could not be loaded. Press Confirm Redemption to try again, or contact your U.S. LawShield representative.",
        );
        return;
      }
      setResult(redemptionResult);
      if (!redemptionResult.success) {
        // Network failure — enqueue for automatic retry. Validation
        // already passed (we're past the validate gate inside
        // redeemVoucher), so there's a live voucher behind this attempt
        // and a retry has a real chance of succeeding once connectivity
        // returns. Business failures are NOT enqueued — they won't change
        // on retry.
        if (redemptionResult.failureKind === "network") {
          const entry = enqueueRedemption(
            redemptionPayload,
            queueDisplayContext,
          );
          setRetryQueue(getQueue());
          if (entry) {
            showError(
              `Connection lost — your redemption for ${voucherCode} has been queued and will retry automatically when you're back online.`,
            );
          } else {
            showError(
              `Queue is full (${QUEUE_BOUNDS.QUEUE_CAP} max). Please process or clear pending retries before submitting more.`,
            );
          }
        } else {
          showError(redemptionResult.message || "Failed to redeem voucher");
        }
      }
    } catch (error: unknown) {
      console.error("Redemption error:", error);
      // Untyped catch — this only hits if redeemVoucher itself throws
      // (it normally returns a typed result). Treat as network and queue.
      const fallbackPayload: RedeemVoucherInput = redemptionPayload;
      const fallbackContext = {
        isEventVoucher,
        discountValue: validationResult.voucher.discount_value,
      };
      const entry = enqueueRedemption(fallbackPayload, fallbackContext);
      setRetryQueue(getQueue());
      const errorMessage =
        error instanceof Error ? error.message : "Failed to redeem voucher";
      showError(
        entry
          ? `${errorMessage} — queued for retry.`
          : `${errorMessage} — queue is full, please clear pending retries.`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRetryQueueNow = async () => {
    if (isProcessingQueue) {
      return;
    }
    setIsProcessingQueue(true);
    try {
      const outcome = await processQueue({
        onSuccess: (_entry, detail) => {
          applyQueuedSuccess(detail);
        },
        onAlreadyRedeemed: (entry) => {
          const isEvent = entry.displayContext?.isEventVoucher ?? false;
          applyQueuedSuccess({
            voucherCode: entry.voucherCode,
            appliedAmount:
              entry.payload.applied_amount ??
              entry.displayContext?.discountValue ??
              0,
            redeemedBy: isEvent
              ? entry.payload.vendor_name || entry.payload.booth_id || ""
              : entry.payload.redeemed_by || "",
            displayContext: entry.displayContext,
          });
        },
      });
      setRetryQueue(getQueue());
      // Success / already-redeemed cases now switch the page to the
      // confirmation screen via applyQueuedSuccess(); only surface a
      // modal for "still failing" outcomes that need user awareness.
      if (
        outcome.succeeded === 0 &&
        outcome.alreadyRedeemed === 0 &&
        outcome.networkFailures > 0
      ) {
        showError("Still no connection. Will keep retrying automatically.");
      } else if (
        outcome.succeeded === 0 &&
        outcome.alreadyRedeemed === 0 &&
        outcome.businessFailures > 0
      ) {
        showError(
          `${outcome.businessFailures} redemption${outcome.businessFailures === 1 ? " was" : "s were"} rejected by the server. See banner for details.`,
        );
      }
    } finally {
      setIsProcessingQueue(false);
    }
  };

  const handleDismissQueueEntry = (id: string) => {
    removeEntry(id);
    setRetryQueue(getQueue());
  };

  const startScanner = async () => {
    try {
      setScanning(true);

      // Check if we're on HTTPS or localhost
      const isSecureContext =
        window.isSecureContext ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isSecureContext) {
        setScanning(false);
        showError(
          "Camera access requires HTTPS. Please enter the voucher code manually or use the QA/Production site which has HTTPS enabled.",
        );
        return;
      }

      // Wait a tick for the DOM to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      const scanner = new Html5Qrcode("qr-reader");
      html5QrcodeRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          console.log("QR Code scanned:", decodedText);
          const code = extractVoucherCode(decodedText);
          console.log("Extracted voucher code:", code);
          setVoucherCode(code);
          stopScanner();
          handleValidate(code);
        },
        () => {
          // Ignore continuous scanning errors - callback required by library
        },
      );
    } catch (err: unknown) {
      console.error("Scanner error:", err);
      setScanning(false);

      // Provide helpful error messages
      const errMessage = err instanceof Error ? err.message : String(err);
      if (errMessage.includes("Camera streaming not supported")) {
        showError(
          "Camera access requires HTTPS. Please enter the voucher code manually or use the QA/Production site.",
        );
      } else if (errMessage.includes("Permission denied")) {
        showError(
          "Camera permission was denied. Please enable camera access in your browser settings and try again.",
        );
      } else {
        showError(
          "Failed to start camera. Please enter the voucher code manually.",
        );
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
      } catch (err) {
        console.error("Stop scanner error:", err);
      }
      html5QrcodeRef.current = null;
    }
    setScanning(false);
  };

  const resetForm = () => {
    setVoucherCode("");
    setRedeemedBy("");
    setAppliedAmount("");
    setBoothId("");
    setVendorName("");
    setSelectedLocationId("");
    setLocationSearchTerm("");
    setShowLocationDropdown(false);
    // Reset org selection
    setSelectedOrganizationId("");
    setSelectedSearchEntry(null);
    setOrgSearchTerm("");
    setShowOrgDropdown(false);
    setValidationResult(null);
    setResult(null);
  };

  // Success screen after redemption
  if (result?.success) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#F5F1E8" }}>
        {/* Header */}
        <div
          className="w-full py-6 px-4"
          style={{ backgroundColor: "#A02428" }}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-4">
            <img
              src="/USLSlogonobackground.png"
              alt=""
              className="h-14 w-auto shrink-0"
            />
            <div className="text-center">
              <h1 className="text-3xl font-bold text-white mb-1">
                U.S. LawShield
              </h1>
              <p className="text-white text-sm">Voucher Redemption Portal</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="mb-6">
                <svg
                  className="mx-auto h-20 w-20 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Voucher Redeemed!
              </h2>
              <p className="text-gray-600">
                Your voucher has been successfully redeemed.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Voucher Code</span>
                  <p className="font-mono font-bold text-lg text-gray-900">
                    {voucherCode}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Amount Applied</span>
                  <p className="font-bold text-lg text-green-600">
                    {formatCurrency(parseFloat(appliedAmount))}
                  </p>
                </div>
                {redeemedBy && (
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">
                      {requireBusinessName ? "Business Name" : "Redeemed By"}
                    </span>
                    <p className="font-medium text-gray-900">{redeemedBy}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Date & Time</span>
                  <p className="font-medium text-gray-900">
                    {formatLocalDate()}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={resetForm}
              className="w-full text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#A02428" }}
            >
              Redeem Another Voucher
            </button>

            <div className="mt-8 text-center text-xs text-gray-500">
              © 2026 U.S. LawShield. All rights reserved.
              <br />
              For assistance, please contact your U.S. LawShield representative.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F1E8" }}>
      {/* Header */}
      <div className="w-full py-6 px-4" style={{ backgroundColor: "#A02428" }}>
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-4">
          <img
            src="/USLSlogonobackground.png"
            alt=""
            className="h-14 w-auto shrink-0"
          />
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-1">
              U.S. LawShield
            </h1>
            <p className="text-white text-sm">Voucher Redemption Portal</p>
          </div>
        </div>
      </div>

      {/* Retry queue banner — shown when there are pending or failed
          redemption attempts persisted in localStorage */}
      {retryQueue.length > 0 && (
        <div className="w-full px-4 pt-4">
          <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-semibold text-amber-900 text-sm">
                  {retryQueue.length} redemption
                  {retryQueue.length === 1 ? "" : "s"} pending retry
                </div>
                <div className="text-xs text-amber-800 mt-1">
                  {isProcessingQueue
                    ? "Retrying now…"
                    : typeof navigator !== "undefined" &&
                        navigator.onLine === false
                      ? "Currently offline — will retry automatically when connection returns."
                      : "Will retry automatically. Click Retry Now to attempt immediately."}
                </div>
              </div>
              <button
                type="button"
                onClick={handleRetryQueueNow}
                disabled={isProcessingQueue}
                className="px-3 py-1 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {isProcessingQueue ? "Retrying…" : "Retry Now"}
              </button>
            </div>
            <ul className="mt-3 space-y-1">
              {retryQueue.map((entry) => (
                <li
                  key={entry.id}
                  className="text-xs text-amber-900 flex items-center justify-between gap-2"
                >
                  <span className="font-mono">{entry.voucherCode}</span>
                  <span className="text-amber-700">
                    {entry.status === "failed"
                      ? `failed after ${entry.attemptCount} attempts${entry.lastError ? ` — ${entry.lastError}` : ""}`
                      : entry.status === "retrying"
                        ? "retrying…"
                        : entry.attemptCount > 0
                          ? `pending (${entry.attemptCount}/${QUEUE_BOUNDS.MAX_ATTEMPTS} attempts)`
                          : "pending"}
                  </span>
                  {entry.status === "failed" && (
                    <button
                      type="button"
                      onClick={() => handleDismissQueueEntry(entry.id)}
                      className="text-amber-700 underline hover:text-amber-900"
                    >
                      dismiss
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: "#1E3A8A" }}
            >
              Redeem Your Voucher
            </h2>
            <p className="text-gray-600 text-sm">
              Scan the QR code or enter the voucher code manually to redeem your
              reward.
            </p>
          </div>

          {/* Main Form - Only show if no validation result */}
          {!validationResult && (
            <div className="space-y-6">
              {/* QR Scanner Section */}
              <div>
                {scanning && (
                  <div
                    className="mb-4 border-2 rounded-lg overflow-hidden"
                    style={{ borderColor: "#A02428" }}
                  >
                    <div id="qr-reader" ref={scannerRef}></div>
                  </div>
                )}

                <button
                  onClick={scanning ? stopScanner : startScanner}
                  disabled={loading}
                  className="w-full py-3 rounded-lg font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: scanning ? "#DC2626" : "#A02428" }}
                >
                  {!scanning && (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                      />
                    </svg>
                  )}
                  {scanning ? "Stop Scanner" : "Scan QR Code"}
                </button>
              </div>

              {/* OR Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">
                    OR
                  </span>
                </div>
              </div>

              {/* Voucher Code Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Voucher Code
                </label>
                <input
                  type="text"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono text-lg text-center"
                  placeholder="ABCD1234"
                  disabled={loading || scanning}
                  maxLength={16}
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Enter the 8-character voucher code
                </p>
              </div>

              {/* Check Voucher Button */}
              <button
                onClick={() => handleValidate()}
                disabled={loading || !voucherCode || scanning}
                className="w-full bg-gray-400 text-white font-semibold py-3 rounded-lg hover:bg-gray-500 disabled:bg-gray-300 transition-colors"
              >
                {loading ? "Validating..." : "Check Voucher"}
              </button>
            </div>
          )}

          {/* Validation Error */}
          {validationResult && !validationResult.valid && (
            <div className="space-y-4">
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <div className="flex items-center mb-2">
                  <svg
                    className="h-6 w-6 text-red-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="font-bold text-red-900">
                    {validationResult.title ?? "Invalid Voucher"}
                  </h3>
                </div>
                <p className="text-red-800">{validationResult.error}</p>
              </div>
              <button
                onClick={resetForm}
                className="w-full text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "#A02428" }}
              >
                Try Another Code
              </button>
            </div>
          )}

          {/* Valid Voucher - Redemption Form */}
          {validationResult?.valid && validationResult.voucher && !result && (
            <div className="space-y-6">
              {/* Success Badge */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <svg
                    className="h-6 w-6 text-green-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="font-bold text-green-900">Valid Voucher!</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-600">Voucher Code:</span>
                    <p className="font-mono font-bold text-lg text-gray-900">
                      {voucherCode}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-600">Discount Value:</span>
                      <p className="font-bold text-lg text-green-700">
                        {formatCurrency(
                          validationResult.voucher.discount_value,
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Expires:</span>
                      <p className="font-semibold text-gray-900">
                        {validationResult.voucher.end_date
                          ? formatDate(validationResult.voucher.end_date)
                          : "No Expiration"}
                      </p>
                    </div>
                  </div>
                  {/* Show voucher source for event vouchers only; retail
                      vouchers intentionally suppress this section */}
                  {validationResult.voucher.event_id &&
                    validationResult.voucher.voucher_source && (
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <span className="text-gray-600 text-xs uppercase tracking-wide">
                          Voucher Source
                        </span>
                        <p className="font-semibold text-gray-900 mt-1">
                          {validationResult.voucher.voucher_source}
                        </p>
                      </div>
                    )}
                  {/* Show different messaging based on redemption scope */}
                  {requiresOrganizationSelection ? (
                    <div className="border-t border-green-200 pt-3 mt-3">
                      <span className="text-gray-600">
                        This voucher code{" "}
                        <span className="font-mono font-bold">
                          {voucherCode}
                        </span>{" "}
                        has been confirmed.{" "}
                        <span className="font-semibold text-purple-700">
                          Please select your organization below
                        </span>{" "}
                        to complete redemption.
                      </span>
                    </div>
                  ) : (
                    validationResult.voucher.organization_name &&
                    validationResult.voucher.organization_name !==
                      "Unknown Organization" && (
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <span className="text-gray-600">
                          <span className="font-bold text-red-600">
                            Scroll down
                          </span>{" "}
                          to confirm the redemption of voucher code{" "}
                          <span className="font-mono font-bold">
                            {voucherCode}
                          </span>
                          . Redemption will be credited to:
                        </span>
                        <p className="font-semibold text-gray-900 mt-3">
                          {validationResult.voucher.organization_name}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Organization Selection - Required for multi-org vouchers (moved above Redeemed By) */}
              {requiresOrganizationSelection && organizationSearch && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <span className="text-sm font-semibold text-purple-800">
                      Select Your Organization{" "}
                      <span className="text-red-500">*</span>
                    </span>
                  </div>
                  <p className="text-xs text-purple-700 mb-3">
                    This voucher can be redeemed at multiple organizations.
                    Search for the organization where this voucher is being
                    redeemed.
                  </p>
                  <RedemptionEntrySearch
                    voucherCode={voucherCode}
                    selected={selectedSearchEntry}
                    onSelect={(e) => {
                      setSelectedSearchEntry(e);
                      setSelectedOrganizationId(e ? e.entry_id : "");
                    }}
                  />
                </div>
              )}
              {requiresOrganizationSelection &&
                facilityShared &&
                availableOrganizations.length > 0 && (
                  <div
                    className="bg-amber-50 border border-amber-300 rounded-lg p-4"
                    data-testid="shared-facility-picker"
                  >
                    <div className="mb-2">
                      <span className="text-sm font-semibold text-amber-900">
                        Choose your store{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    </div>
                    <p className="text-xs text-amber-800 mb-3">
                      This voucher&apos;s retailer ID is listed under more than
                      one store. Select the store where it is being redeemed.
                    </p>
                    <div
                      role="radiogroup"
                      aria-label="Store where this voucher is being redeemed"
                      className="space-y-2 max-h-80 overflow-y-auto"
                    >
                      {availableOrganizations.map((org) => {
                        const checked = selectedOrganizationId === org.id;
                        const place = [org.city, org.state]
                          .filter(Boolean)
                          .join(", ");
                        return (
                          <button
                            key={org.id}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            onClick={() => setSelectedOrganizationId(org.id)}
                            className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                              checked
                                ? "border-amber-500 bg-amber-100 ring-2 ring-amber-400"
                                : "border-gray-300 bg-white hover:bg-amber-50"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-gray-900">
                                {org.name}
                              </span>
                              {checked && (
                                <span className="text-xs font-semibold text-amber-800 whitespace-nowrap">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">
                              {[org.address, place].filter(Boolean).join(" · ")}
                            </div>
                            <div className="text-xs text-gray-500">
                              {org.location_id
                                ? `Store of ${org.organization_name || "an organization"}`
                                : "Organization account (no store listed)"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              {requiresOrganizationSelection &&
                !organizationSearch &&
                !facilityShared &&
                availableOrganizations.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <svg
                        className="h-5 w-5 text-purple-600 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-purple-800">
                        Select Your Organization{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    </div>
                    <p className="text-xs text-purple-700 mb-3">
                      This voucher can be redeemed at multiple organizations.
                      Please select where this voucher is being redeemed.
                    </p>
                    <div className="relative" ref={orgDropdownRef}>
                      <input
                        type="text"
                        value={
                          selectedOrganization
                            ? `${selectedOrganization.name}${selectedOrganization.city ? ` - ${selectedOrganization.city}, ${selectedOrganization.state}` : ""}`
                            : orgSearchTerm
                        }
                        onChange={(e) => {
                          setOrgSearchTerm(e.target.value);
                          setSelectedOrganizationId("");
                          setShowOrgDropdown(true);
                        }}
                        onFocus={() => setShowOrgDropdown(true)}
                        placeholder="Search by name, address, city, or state..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      />
                      {selectedOrganizationId && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrganizationId("");
                            setOrgSearchTerm("");
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                      {showOrgDropdown && filteredOrganizations.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredOrganizations.slice(0, 50).map((org) => (
                            <button
                              key={org.id}
                              type="button"
                              onClick={() => {
                                setSelectedOrganizationId(org.id);
                                setOrgSearchTerm("");
                                setShowOrgDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors ${
                                selectedOrganizationId === org.id
                                  ? "bg-purple-100 text-purple-800"
                                  : "text-gray-900"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{org.name}</span>
                                {org.distance !== undefined && (
                                  <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full ml-2">
                                    {org.distance.toFixed(1)} mi
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {org.address && <span>{org.address}, </span>}
                                {org.city && (
                                  <span>
                                    {org.city}, {org.state}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                          {filteredOrganizations.length > 50 && (
                            <div className="px-4 py-2 text-xs text-gray-500 border-t">
                              Showing first 50 results. Type to narrow down.
                            </div>
                          )}
                        </div>
                      )}
                      {showOrgDropdown &&
                        filteredOrganizations.length === 0 &&
                        orgSearchTerm && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-gray-500 text-sm">
                            No organizations found matching "{orgSearchTerm}"
                          </div>
                        )}
                    </div>
                  </div>
                )}

              {/* Business Name (event vouchers + ticket rewards) / Redeemed By (other vouchers) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {requireBusinessName ? "Business Name" : "Redeemed By"}{" "}
                  {requireBusinessName ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-500 font-normal">
                      (optional)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={redeemedBy}
                  onChange={(e) => setRedeemedBy(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={
                    requireBusinessName
                      ? "Enter the business name"
                      : "Enter name or identifier"
                  }
                />
                {requireBusinessName && (
                  <p className="text-xs text-red-600 mt-1">
                    Your business name is required in order to claim your
                    reimbursement. If you need help, please go talk to your USLS
                    representative for assistance.
                  </p>
                )}
              </div>

              {/* Applied Voucher Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Applied Voucher Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                    $
                  </span>
                  <input
                    type="text"
                    value={appliedAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and decimal point
                      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                        setAppliedAmount(value);
                      }
                    }}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-lg font-semibold"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Defaulted to voucher value. You can change it to a lesser
                  amount if needed (max{" "}
                  {formatCurrency(validationResult.voucher.discount_value)}).
                  Remainder amounts will not carry over to future transactions;
                  each voucher discount can only be used once.
                </p>
              </div>

              {/* Location Selection - Required for org-level vouchers with multiple locations */}
              {requiresLocationSelection && availableLocations.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <svg
                      className="h-5 w-5 text-blue-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="text-sm font-semibold text-blue-800">
                      Select Redemption Location{" "}
                      <span className="text-red-500">*</span>
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    This organization has multiple locations. Please select
                    where this voucher is being redeemed.
                  </p>
                  <div className="relative" ref={locationDropdownRef}>
                    <input
                      type="text"
                      value={selectedLocationName || locationSearchTerm}
                      onChange={(e) => {
                        setLocationSearchTerm(e.target.value);
                        setSelectedLocationId("");
                        setShowLocationDropdown(true);
                      }}
                      onFocus={() => setShowLocationDropdown(true)}
                      placeholder="Search or select a location..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    {selectedLocationId && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLocationId("");
                          setLocationSearchTerm("");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                    {showLocationDropdown && filteredLocations.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredLocations.map((loc) => (
                          <button
                            key={loc.id}
                            type="button"
                            onClick={() => {
                              setSelectedLocationId(loc.id);
                              setLocationSearchTerm("");
                              setShowLocationDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors ${
                              selectedLocationId === loc.id
                                ? "bg-blue-100 text-blue-800"
                                : "text-gray-900"
                            }`}
                          >
                            {loc.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {showLocationDropdown &&
                      filteredLocations.length === 0 &&
                      locationSearchTerm && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-gray-500 text-sm">
                          No locations found matching "{locationSearchTerm}"
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Confirm Redemption Button */}
              <button
                onClick={handleRedeem}
                disabled={loading}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors text-lg"
              >
                {loading ? "Processing..." : "Confirm Redemption"}
              </button>

              {/* Cancel Button */}
              <button
                onClick={resetForm}
                className="w-full text-gray-600 py-3 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-gray-500">
            © 2026 U.S. LawShield. All rights reserved.
            <br />
            For assistance, please contact your U.S. LawShield representative.
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Error
                </h3>
                <p className="text-sm text-gray-700">{errorModal.message}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={closeError}
                className="px-6 py-2 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "#A02428" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherRedemption;
